import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Cookie, Shield, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const COOKIE_CONSENT_KEY = 'tradejourn_cookie_consent';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
          data-testid="cookie-consent-banner"
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
              {/* Main Banner */}
              <div className="p-4 md:p-6">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="hidden sm:flex h-12 w-12 rounded-xl bg-emerald-500/10 items-center justify-center flex-shrink-0">
                    <Cookie className="h-6 w-6 text-emerald-400" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Cookie className="h-5 w-5 text-emerald-400 sm:hidden" />
                      <h3 className="font-semibold text-white">Cookie-Einstellungen</h3>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                      Wir verwenden Cookies, um dir die bestmögliche Erfahrung auf unserer Plattform zu bieten. 
                      Einige Cookies sind für den Betrieb der Website notwendig, während andere uns helfen, 
                      die Nutzung zu analysieren und unseren Service zu verbessern.
                    </p>
                    
                    {/* Links */}
                    <div className="flex flex-wrap gap-3 mt-3 text-sm">
                      <Link 
                        to="/datenschutz" 
                        className="text-emerald-400 hover:text-emerald-300 hover:underline inline-flex items-center gap-1"
                        data-testid="cookie-privacy-link"
                      >
                        <Shield className="h-3.5 w-3.5" />
                        Datenschutzerklärung
                      </Link>
                      <button 
                        onClick={() => setShowDetails(!showDetails)}
                        className="text-zinc-400 hover:text-zinc-300 hover:underline"
                        data-testid="cookie-details-toggle"
                      >
                        {showDetails ? 'Details ausblenden' : 'Details anzeigen'}
                      </button>
                    </div>
                  </div>

                  {/* Close Button (Mobile) */}
                  <button 
                    onClick={handleAcceptNecessary}
                    className="sm:hidden p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
                    aria-label="Schließen"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Details Section */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 pt-4 border-t border-zinc-800 grid gap-3 sm:grid-cols-3">
                        {/* Necessary Cookies */}
                        <div className="p-3 rounded-lg bg-zinc-800/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-white">Notwendig</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                              Immer aktiv
                            </span>
                          </div>
                          <p className="text-xs text-zinc-500">
                            Diese Cookies sind für den Betrieb der Website unbedingt erforderlich (Anmeldung, Sicherheit).
                          </p>
                        </div>

                        {/* Analytics Cookies */}
                        <div className="p-3 rounded-lg bg-zinc-800/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-white">Analyse</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-400">
                              Optional
                            </span>
                          </div>
                          <p className="text-xs text-zinc-500">
                            Helfen uns zu verstehen, wie Besucher unsere Website nutzen.
                          </p>
                        </div>

                        {/* Payment Cookies */}
                        <div className="p-3 rounded-lg bg-zinc-800/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-white">Zahlungen</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                              Stripe
                            </span>
                          </div>
                          <p className="text-xs text-zinc-500">
                            Erforderlich für sichere Zahlungsabwicklung über Stripe.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-2 mt-4 pt-4 border-t border-zinc-800">
                  <Button
                    variant="outline"
                    onClick={handleAcceptNecessary}
                    className="flex-1 border-zinc-700 hover:bg-zinc-800 text-zinc-300"
                    data-testid="cookie-accept-necessary"
                  >
                    Nur notwendige
                  </Button>
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                    data-testid="cookie-accept-all"
                  >
                    Alle akzeptieren
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
